import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from 'ui-library';

export type KydConfigureChoice = 'same' | 'each' | null;

// Shown either right after creating several KYD at once (see kyd.component.ts's
// promptConfigureNewKyds()) - a brand new KYD has no settings at all yet (the site can't reach
// out over Bluetooth to read anything), so this offers to configure them right away rather
// than leaving them all showing "not configured" until someone opens each one individually -
// or when editing an existing multi-select (bulkConfigureKyd()), which needs its own wording:
// there's nothing "new"/uncreated about the selection, and "later" doesn't fit an edit action
// the way it does a just-finished creation.
@Component({
  selector: 'app-kyd-configure-choice-dialog',
  imports: [CommonModule, TranslateModule, MatDialogModule, MatIconModule, MatToolbar, ButtonComponent],
  standalone: true,
  templateUrl: './kyd-configure-choice-dialog.component.html',
  styleUrls: ['./kyd-configure-choice-dialog.component.scss']
})
export class KydConfigureChoiceDialogComponent {
  // allowSame is omitted (undefined) whenever it doesn't apply (e.g. a fresh KYD batch is always
  // uniform) and explicitly false only when the caller has a mixed-type selection, for which a
  // single shared settings form can't represent every device at once.
  readonly data: { count: number, context?: 'new' | 'edit' | 'applyTemplate', allowSame?: boolean } = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<KydConfigureChoiceDialogComponent, KydConfigureChoice> = inject(MatDialogRef<KydConfigureChoiceDialogComponent, KydConfigureChoice>);

  get titleKey(): string {
    if (this.data.context === 'edit') return 'trackers.edit_selected_kyds_title';
    if (this.data.context === 'applyTemplate') return 'trackers.apply_template_selection_title';
    return 'trackers.configure_new_kyds_title';
  }

  get messageKey(): string {
    if (this.data.context === 'edit') return 'trackers.edit_selected_kyds_message';
    if (this.data.context === 'applyTemplate') return 'trackers.apply_template_selection_message';
    return 'trackers.configure_new_kyds_message';
  }

  get cancelKey(): string {
    return this.data.context === 'new' ? 'trackers.configure_new_kyds_later' : 'trackers.edit_selected_kyds_cancel';
  }

  choose(choice: KydConfigureChoice) {
    this.dialogRef.close(choice);
  }
}
